import { callAI, callAIGenerate } from '../lib/openai';
import { validateInput } from '../utils/inputValidation';

function getErrorMessage(error) {
  const type = error?.type;
  if (type === 'LimitReached') {
    // Pass through with full data for the UpgradeModal
    return { isInternal: true, isLimitReached: true, message: error.message, used: error.used, limit: error.limit, daysUntilReset: error.daysUntilReset };
  } else if (type === 'AuthenticationError') {
    return { isInternal: true, message: 'Authentication failed. Please sign in again.' };
  } else if (type === 'PermissionDeniedError') {
    return { isInternal: true, message: 'Permission denied.' };
  } else if (type === 'RateLimitError') {
    return { isInternal: true, message: 'Too many requests. Please wait a moment.' };
  } else if (type === 'InternalServerError') {
    return { isInternal: true, message: 'Service temporarily unavailable. Please try again later.' };
  } else {
    return { isInternal: false, message: error?.message || 'Something went wrong. Please try again.' };
  }
}

// Character limits per platform
const PLATFORM_LIMITS = {
  linkedin: 3000,
  twitter: 280,
  bluesky: 300,
  threads: 500,
};

// Anti-cringe phrases to strip client-side as a safety net
const FORBIDDEN_PHRASES = [
  'game-changer', 'revolutionizing', 'unprecedented', 'supercharge',
  'game changer', 'disruptive', 'paradigm shift', 'synergy',
  'leverage', 'low-hanging fruit', 'move the needle', 'growth hack',
];

/**
 * Generate platform-optimized technical content.
 * Prompts and model selection are handled server-side.
 * Only raw data is sent from the client.
 */
export async function generateTechnicalContent(roughIdea, codeSnippet, style, styleDetails, platforms = ['linkedin', 'twitter'], additionalInstructions = '', githubUrl = '') {
  try {
    // ── Client-side Tier 1 validation (instant feedback) ──
    const validation = validateInput(roughIdea, codeSnippet);
    if (!validation.valid) {
      throw Object.assign(new Error(validation.reason), {
        type: 'InputQuality',
        score: validation.score,
      });
    }

    const response = await callAIGenerate({
      roughIdea,
      codeSnippet: codeSnippet || undefined,
      platforms,
      styleDetails: styleDetails || undefined,
      additionalInstructions: additionalInstructions || undefined,
      githubUrl: githubUrl || undefined,
    });

    const rawContent = response?.choices?.[0]?.message?.content;
    if (!rawContent) {
      throw new Error('No content received. Please try again.');
    }

    let generatedContent;
    try {
      generatedContent = JSON.parse(rawContent);
    } catch {
      throw new Error('Failed to process AI response. Please try again.');
    }

    // Extract quality metadata from AI response
    const inputQuality = generatedContent?.input_quality;
    const confidence = generatedContent?.confidence;
    const assumptions = generatedContent?.assumptions || [];

    // Post-process: safety net for forbidden phrases and character limits
    const filteredContent = {};
    const metadataKeys = ['input_quality', 'confidence', 'assumptions'];

    Object.keys(generatedContent)?.forEach(key => {
      if (metadataKeys.includes(key)) return; // skip metadata fields

      let content = generatedContent?.[key];
      if (typeof content !== 'string') return;

      // Strip forbidden phrases
      FORBIDDEN_PHRASES?.forEach(phrase => {
        const regex = new RegExp(phrase, 'gi');
        content = content?.replace(regex, '');
      });

      // Enforce character limits
      const maxLimit = PLATFORM_LIMITS[key];
      if (maxLimit && content?.length > maxLimit) {
        console.warn(`Character limit exceeded for ${key}: ${content.length}/${maxLimit} — trimming`);
        let trimmed = content.substring(0, maxLimit);
        const lastSpace = trimmed.lastIndexOf(' ');
        if (lastSpace > maxLimit * 0.7) trimmed = trimmed.substring(0, lastSpace);
        content = trimmed.replace(/[\s,.;:!?\-—]+$/, '');
      }

      filteredContent[key] = content;
    });

    // Attach quality metadata to the response
    filteredContent._meta = {
      inputQuality: inputQuality || 'meaningful',
      confidence: confidence || 1.0,
      assumptions: assumptions,
    };

    return filteredContent;
  } catch (error) {
    // Handle input quality rejection from server (422)
    if (error?.type === 'InputQuality') {
      throw error; // pass through client-side validation errors
    }
    const errorInfo = getErrorMessage(error);
    if (errorInfo.isLimitReached) {
      throw Object.assign(new Error(errorInfo.message), {
        type: 'LimitReached',
        used: errorInfo.used,
        limit: errorInfo.limit,
        daysUntilReset: errorInfo.daysUntilReset,
      });
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Refine existing content (still uses chat mode but prompts are stripped server-side)
 */
export async function refineTechnicalContent(content, platform) {
  try {
    const response = await callAI({
      messages: [
        { role: 'user', content: `Refine this ${platform} post to improve engagement while maintaining technical accuracy:\n\n${content}\n\nImprove hook, clarity, and add a question to spark debate.` }
      ],
      platforms: [platform],
    });

    let refinedContent = response?.choices?.[0]?.message?.content;

    FORBIDDEN_PHRASES?.forEach(phrase => {
      const regex = new RegExp(phrase, 'gi');
      refinedContent = refinedContent?.replace(regex, '');
    });

    return refinedContent;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    throw new Error(errorInfo.message);
  }
}

/**
 * Validate content against rules (client-side only, no sensitive data)
 */
export function validateContentRules(content) {
  const warnings = [];

  FORBIDDEN_PHRASES?.forEach(phrase => {
    const regex = new RegExp(phrase, 'gi');
    if (regex?.test(content)) {
      warnings?.push(`Contains phrase that may seem generic: "${phrase}"`);
    }
  });

  const hasHook = /^.{0,200}(problem|issue|mistake|challenge|pain)/i?.test(content);
  const hasTradeoff = /(trade-?off|gave up|sacrifice|cost|downside)/i?.test(content);

  if (!hasHook) warnings?.push('Consider adding a stronger hook');
  if (!hasTradeoff) warnings?.push('Consider mentioning trade-offs');

  return warnings;
}
